import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Two-tone chime
    const playTone = (freq, startTime, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    playTone(880, now, 0.15);
    playTone(1100, now + 0.15, 0.2);
    playTone(1320, now + 0.3, 0.3);
  } catch (e) {
    // console.warn('Audio playback not supported:', e);
  }
}

export function useAdminNotifications(enabled) {
  const [pendingOrders, setPendingOrders] = useState(0);
  const [pendingReservations, setPendingReservations] = useState(0);
  const eventSourceRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('notif_sound') !== 'off';
    }
    return true;
  });

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      localStorage.setItem('notif_sound', next ? 'on' : 'off');
      return next;
    });
  }, []);

  // Fetch pending counts
  const fetchPending = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/notifications/pending`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setPendingOrders(data.pending_orders);
        setPendingReservations(data.pending_reservations);
      }
    } catch (e) {
      // console.warn('Failed to fetch pending notifications:', e);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchPending();
    const interval = setInterval(fetchPending, 15000);
    return () => clearInterval(interval);
  }, [enabled, fetchPending]);

  // SSE connection
  useEffect(() => {
    if (!enabled) return;

    const connect = () => {
      const url = `${BACKEND_URL}/api/admin/notifications/stream`;
      const es = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'new_order') {
            if (soundEnabled) playNotificationSound();
            toast.success(
              `Nouvelle commande de ${msg.data.customer_name} — ${msg.data.total?.toFixed(2)}€ — Retrait ${msg.data.pickup_time}`,
              { duration: 8000, icon: null }
            );
            fetchPending();
          } else if (msg.type === 'new_reservation') {
            if (soundEnabled) playNotificationSound();
            toast.info(
              `Nouvelle réservation de ${msg.data.name} — ${msg.data.date} à ${msg.data.time} — ${msg.data.guests} pers.`,
              { duration: 8000, icon: null }
            );
            fetchPending();
          }
        } catch (e) {
          // console.warn('Failed to parse SSE message:', e);
        }
      };

      es.onerror = () => {
        es.close();
        // Reconnect after 10s
        setTimeout(connect, 10000);
      };
    };

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [enabled, soundEnabled, fetchPending]);

  return { pendingOrders, pendingReservations, totalPending: pendingOrders + pendingReservations, soundEnabled, toggleSound, fetchPending };
}
